import { useForm } from '@inertiajs/react'
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  Field,
  Label,
  Input,
  Select,
  Button,
  ErrorMessage,
} from '@/components/ui'
import { AssignmentList } from '@/components/common/AssignmentList'
import { useTranslations } from '@/lib/i18n'

interface AssignableUser {
  id: number
  name: string
  email: string
  role: string
}

/**
 * "Add merchant" create dialog, opened from the Merchants index. Submits a
 * POST; on validation error the controller redirects back with Inertia errors,
 * which keeps the dialog open and populates the field messages.
 */
export function MerchantFormModal({
  open,
  onClose,
  statuses,
  assignableUsers,
}: {
  open: boolean
  onClose: () => void
  statuses: string[]
  assignableUsers: AssignableUser[]
}) {
  const { t } = useTranslations()
  const form = useForm<{ name: string; status: string; user_ids: number[] }>({
    name: '',
    status: statuses[0] ?? 'active',
    user_ids: [],
  })

  function close() {
    form.reset()
    form.clearErrors()
    onClose()
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    form.transform((data) => ({ merchant: data }))
    form.post('/merchants', { preserveScroll: true, onSuccess: close })
  }

  function toggleUser(id: number, checked: boolean) {
    form.setData('user_ids', checked ? [...form.data.user_ids, id] : form.data.user_ids.filter((x) => x !== id))
  }

  return (
    <Dialog open={open} onClose={close}>
      <DialogTitle>{t('merchants.new_title')}</DialogTitle>
      <form onSubmit={submit}>
        <DialogBody className="flex flex-col gap-5">
          <Field>
            <Label>{t('merchants.name')}</Label>
            <Input value={form.data.name} onChange={(e) => form.setData('name', e.target.value)} autoFocus />
            {form.errors.name && <ErrorMessage>{form.errors.name}</ErrorMessage>}
          </Field>

          <Field>
            <Label>{t('merchants.status')}</Label>
            <Select value={form.data.status} onChange={(e) => form.setData('status', e.target.value)}>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {t(`statuses.${status}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label>{t('merchants.assigned_users')}</Label>
            <AssignmentList
              items={assignableUsers.map((u) => ({ id: u.id, label: u.name, sublabel: u.email }))}
              selected={form.data.user_ids}
              onToggle={toggleUser}
              searchPlaceholder={t('common.search')}
            />
          </Field>
        </DialogBody>

        <DialogActions>
          <Button plain type="button" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" color="brand" disabled={form.processing}>
            {t('common.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
